import { z } from "zod/v4";

import { AvailableSubscriptionDiscountTypeList } from "../dtos/subscriptions";
import {
  castToCommaList,
  castToNumber,
  castToString,
  validateIdNotExponent,
} from "../utils";

export const TestProductInputStateList = ["price", "compareAtPrice"] as const;
export type TestProductInputStateType =
  (typeof TestProductInputStateList)[number];

export const FileBuildTestGroupStateSchema = z.object({
  name: z.string(),
  percentage: z.number(),
  order: z.number(),
});
export type FileBuildTestGroupStateType = z.infer<
  typeof FileBuildTestGroupStateSchema
>;

// Test Groups within Variants
export const ProductVariantEventStatusList = [
  "pending",
  "failed",
  "successful",
] as const;
export type ProductVariantEventStatus =
  | (typeof ProductVariantEventStatusList)[number]
  | null
  | undefined;

export const BuildFileTypesList = ["csv", "excel"] as const;
export type BuildFileType = (typeof BuildFileTypesList)[number];

export const CogsExcelUploadSchema = z.object({
  productId: validateIdNotExponent,
  variantId: validateIdNotExponent,
  cogs: castToNumber,
  shopifyDomain: z.string().nullish().default(""),
  updatedAtTs: z.coerce.number().nullish().default(0),
  productGroup: z.string().nullish().default(""),
});
export type CogsExcelUploadRow = z.infer<typeof CogsExcelUploadSchema>;

/**
 * Unified COGS row schema used throughout the upload/download pipeline.
 * - Core fields (productId, variantId) are required.
 * - Display fields (titles, sku, tags, etc.) are present in download templates and Shopify responses.
 * - Internal fields (shopifyDomain, updatedAtTs, etc.) are set during processing, not in user-facing files.
 */
export const CogsRowSchema = z.object({
  productId: z.string().optional(),
  variantId: z.string().optional(),
  cogs: z.union([z.string(), z.number()]).optional(),

  // Display fields — present in download templates and Shopify responses
  productTitle: z.string().optional(),
  variantTitle: z.string().optional(),
  sku: z.string().nullish(),
  tags: z.union([z.string(), z.array(z.string())]).optional(),
  shopifyProductType: z.string().optional(),
  status: z.string().optional(),
  productGroup: z.string().optional(),

  // Internal fields — stripped from user-facing templates, injected during processing
  shopifyDomain: z.string().optional(),
  updatedAtTs: z.union([z.number(), z.string()]).optional(),
  handle: z.string().optional(),
  inventory: z.number().nullish(),
  productType: z.string().optional(),
  price: z.union([z.string(), z.number()]).optional(),
  compareAtPrice: z.union([z.string(), z.number()]).optional(),
});
export type CogsRow = z.infer<typeof CogsRowSchema>;

export const PriceUploadCsvSchema = z.object({
  product_id: z.string(),
  variant_id: z.string(),
  sku: z.string().nullish(),
  new_price: castToNumber.nullish(),
  new_compare_price: castToNumber.nullish(),
  shopify_subdomain: z.string().nullish(),
});
export type PriceUploadCsvRow = z.infer<typeof PriceUploadCsvSchema>;

export const ExperienceFileSchema = z.object({
  productTitle: castToString,
  productId: validateIdNotExponent,
  variantTitle: castToString,
  variantId: validateIdNotExponent,
  sku: z.string().nullish(),
  handle: z.string().nullish(),
  inventory: castToNumber.nullish(),
  tags: z.string().nullish(),
  productType: z.string().nullish(),
  status: z.string().nullish(),
  price: castToNumber.nullish(),
  comparePrice: castToNumber.nullish(),
  cogs: castToNumber.nullish(),
  subscriptionDiscount: castToNumber.or(z.string()).nullish(),
  subscriptionDiscountType: z
    .enum(AvailableSubscriptionDiscountTypeList)
    .nullish(),
});

export type ExperienceFileRow = z.infer<typeof ExperienceFileSchema>;
